/**
 * ApiClientContext — constructs the singleton ApiClient once auth resolves.
 *
 * Wiring contract (teams-app-wiring §2.5):
 *   - Function key goes in the `x-functions-key` header only — never `?code=`.
 *   - `x-pcb-client-id: ROP-teams` is set on every request via the api-client
 *     `context` callback.
 *   - When the user is resolved, the branch/region/role/officer codes are also
 *     propagated as `x-pcb-*` context headers for observability + server-side
 *     audit trails.
 *
 * The client is re-built whenever `isInTeams` flips (Teams SSO vs. MSAL) or the
 * context payload (role / branch / region) changes — but not on every render.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ApiClient, type ApiClientConfig } from '@retail-ops/api-client';
import { getTeamsAuthToken } from '@/services/teams';
import { getMsalToken } from '@/services/msal';
import { useAuth } from './AuthContext';

const ApiClientContext = createContext<ApiClient | null>(null);

/** Version stamp sent as `x-pcb-client-version`. */
const CLIENT_VERSION = 'ROP-teams@0.1.0';

/** Client id used for every `x-pcb-client-id` header. */
const CLIENT_ID = 'ROP-teams';

function resolveBaseUrl(): string {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  if (!baseUrl && import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.error('VITE_API_BASE_URL is not set. The app cannot reach the Function.');
  }
  return baseUrl || 'https://pcb-retail-api.azurewebsites.net/api';
}

function resolveFunctionKey(): string {
  return (import.meta.env.VITE_FUNCTION_KEY as string | undefined) ?? '';
}

export function ApiClientProvider({ children }: { children: ReactNode }): JSX.Element {
  const { isInTeams, user } = useAuth();

  // Pull out stable primitives so the useMemo dep list is flat.
  const branchCode = user?.branchCode;
  const regionCode = user?.regionCode;
  const officerCode = user?.officerCode;
  const userRole = user?.primaryRole;

  const client = useMemo<ApiClient>(() => {
    const cfg: ApiClientConfig = {
      baseUrl: resolveBaseUrl(),
      functionKey: resolveFunctionKey(),
      version: CLIENT_VERSION,
      getToken: async () => {
        if (isInTeams) {
          try {
            return await getTeamsAuthToken();
          } catch {
            // fall through to MSAL
          }
        }
        return getMsalToken();
      },
      context: () => {
        const ctx: {
          clientId: string;
          branchCode?: string;
          regionCode?: string;
          officerCode?: string;
          userRole?: string;
        } = { clientId: CLIENT_ID };
        if (branchCode) ctx.branchCode = branchCode;
        if (regionCode) ctx.regionCode = regionCode;
        if (officerCode) ctx.officerCode = officerCode;
        if (userRole) ctx.userRole = userRole;
        return ctx;
      },
    };
    return new ApiClient(cfg);
  }, [isInTeams, branchCode, regionCode, officerCode, userRole]);

  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

export function useApiClient(): ApiClient {
  const ctx = useContext(ApiClientContext);
  if (!ctx) throw new Error('useApiClient must be used within ApiClientProvider');
  return ctx;
}
